/**
 * 界面文案的语言切换与取词。
 *
 * 背景：控制台与管理后台面向韩国、中国与海外开发者，界面文字要有韩文、中文、英文三版（PRD 7 章 packages/i18n）。
 * 设计意图：
 *   - 文案条目是 { ko, zh, en } 对象，组件里直接引用条目（t(L.keys.title)），漏写某种语言时 TypeScript 报错，
 *     不靠字符串键查表，也就不会出现运行时找不到键。
 *   - 语言优先级：用户手动选过的（localStorage ap_locale）> 账号资料里的 locale > 浏览器语言 > 韩文。
 *   - 文案里的 {name} 占位由 t 的第二个参数替换。
 * 关键约束：只存语言偏好，不存任何账号信息；切换语言不刷新页面。
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

export type Locale = 'ko' | 'zh' | 'en'
export type Entry = { ko: string; zh: string; en: string }
export type Vars = Record<string, string | number>

export const LOCALES: { id: Locale; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'ko', label: '한국어' },
  { id: 'zh', label: '繁體中文' },
]

const STORAGE_KEY = 'ap_locale'

export function normalizeLocaleCode(raw: string | null | undefined): Locale | null {
  if (!raw) return null
  const v = raw.trim().toLowerCase()
  if (v === 'en' || v.startsWith('en-')) return 'en'
  if (v === 'ko' || v.startsWith('ko-')) return 'ko'
  if (v === 'zh' || v.startsWith('zh-')) return 'zh'
  return null
}

export function detectLocale(): Locale {
  try {
    const saved = normalizeLocaleCode(localStorage.getItem(STORAGE_KEY))
    if (saved) return saved
  } catch {
    // 隐私模式下 localStorage 不可用，按浏览器语言走。
  }
  return normalizeLocaleCode(navigator.language) ?? 'en'
}

export function hasSavedLocale(): boolean {
  try {
    return normalizeLocaleCode(localStorage.getItem(STORAGE_KEY)) !== null
  } catch {
    return false
  }
}

export function translate(entry: Entry, locale: Locale, vars?: Vars): string {
  let s = entry[locale] ?? entry.en
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v))
  return s
}

/** 给 Intl 用的语言标签。 */
export function intlTag(locale: Locale): string {
  return locale === 'zh' ? 'zh-TW' : locale === 'ko' ? 'ko-KR' : 'en-US'
}

interface Ctx {
  locale: Locale
  setLocale: (l: Locale, persist?: boolean) => void
  t: (entry: Entry, vars?: Vars) => string
}

const LocaleContext = createContext<Ctx | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setState] = useState<Locale>(detectLocale)
  const setLocale = useCallback((l: Locale, persist = true) => {
    const next = normalizeLocaleCode(l) ?? 'en'
    setState(next)
    document.documentElement.lang = next === 'zh' ? 'zh-TW' : next
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // 存不下就只在本次会话生效。
      }
    }
  }, [])
  const value = useMemo<Ctx>(
    () => ({ locale, setLocale, t: (entry, vars) => translate(entry, locale, vars) }),
    [locale, setLocale],
  )
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useI18n(): Ctx {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useI18n must be used inside <LocaleProvider>')
  return ctx
}
